import React, { useState, useEffect, useCallback } from 'react';
import { PAGE, SettingsHeader } from './SettingsUI';
import {
  Save, Loader2, Plus, Trash2, X, Settings2,
  Pill, ClipboardList, AlertTriangle, Building2,
  Calendar, Clock, Wifi, MessageSquare, FileText, Tag, LayoutGrid,
} from 'lucide-react';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import clsx from 'clsx';
import { useFeatureReload, persistFeatures } from '../../context/FeatureContext';

// ── Toggle ────────────────────────────────────────────────────────────────────
function Toggle({ checked, onChange, disabled }) {
  return (
    <button type="button" onClick={() => !disabled && onChange(!checked)} disabled={disabled}
      className={clsx('relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors focus:outline-none',
        checked ? 'bg-primary-600' : 'bg-slate-200', disabled && 'opacity-50 cursor-not-allowed')}>
      <span className={clsx('inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform',
        checked ? 'translate-x-6' : 'translate-x-1')} />
    </button>
  );
}

// ── FeatureRow ────────────────────────────────────────────────────────────────
function FeatureRow({ label, hint, children }) {
  return (
    <div className="flex items-center justify-between gap-4 py-3 border-b border-slate-100 last:border-0">
      <div>
        <p className="text-sm font-medium text-slate-800">{label}</p>
        {hint && <p className="text-xs leading-relaxed text-slate-500 mt-0.5">{hint}</p>}
      </div>
      <div className="shrink-0">{children}</div>
    </div>
  );
}

// ── FeatureCard ───────────────────────────────────────────────────────────────
function FeatureCard({ title, icon: Icon, children, onSave, saving, action }) {
  return (
    <div className="card p-5 space-y-1">
      <div className="flex items-center justify-between pb-3 border-b border-slate-100 mb-2">
        <div className="flex items-center gap-2.5">
          {Icon && <Icon size={16} strokeWidth={1.9} className="text-slate-500" />}
          <h3 className="text-sm font-semibold text-slate-900">{title}</h3>
        </div>
        <div className="flex items-center gap-2">
          {action}
          {onSave && (
            <button onClick={onSave} disabled={saving} className="btn-secondary btn-sm">
              {saving ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />} Save
            </button>
          )}
        </div>
      </div>
      {children}
    </div>
  );
}

// ── InlineList — add/delete items (durations, categories, task types) ─────────
function InlineList({ items, onAdd, onDelete, placeholder, addLabel = 'Add' }) {
  const [val, setVal] = useState('');
  return (
    <div className="space-y-2 mt-2">
      {items.length === 0 && (
        <p className="text-xs text-slate-400 text-center py-3 border border-dashed border-slate-200 rounded-xl">
          No items configured yet.
        </p>
      )}
      {items.map((item, i) => (
        <div key={i} className="flex items-center justify-between px-3 py-2 bg-slate-50 rounded-xl text-sm text-slate-700">
          <span>{item}</span>
          <button onClick={() => onDelete(i)} className="text-slate-400 hover:text-red-500 transition-colors">
            <X size={13} />
          </button>
        </div>
      ))}
      <div className="flex gap-2 mt-2">
        <input value={val} onChange={e => setVal(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter' && val.trim()) { onAdd(val.trim()); setVal(''); } }}
          placeholder={placeholder} className="input-field flex-1 !py-2 text-sm" />
        <button onClick={() => { if (val.trim()) { onAdd(val.trim()); setVal(''); } }}
          className="btn-primary btn-sm gap-1 px-3">
          <Plus size={13} /> {addLabel}
        </button>
      </div>
    </div>
  );
}

// ── Default feature settings ───────────────────────────────────────────────────
const DEFAULTS = {
  enable_emar: false,
  enable_quotes: true,
  quote_title: 'Quote',
  add_quote_terms: true,
  default_quote_terms: 'The pricing in this quote is based on the NDIS Pricing Arrangements and Price Limits current at the time of quoting. Services will be invoiced in accordance with the NDIS price limits applicable at the time the service is delivered.',
  enable_tasks: true,
  task_durations: [],
  task_support_categories: [],
  task_invoice_format: '{client_full_name} @ {date} {start_time} - {end_time} [{pricebook} {price_ref}] {task_name}',
  require_support_category: false,
  task_types: [],
  enable_incidents: true,
  enable_incidents_carers_app: true,
  enable_facilities: true,
  publish_shifts: true,
  advanced_break_tracking: false,
  offline_clocking: false,
  enable_general_chat: false,
  enable_team_chats: false,
  enable_direct_messaging: false,
  enable_care_signal_alerts_chat: false,
  // Page/navigation toggles (default true = shown until an org turns it off).
  enable_templates: true,
  enable_groups: true,
  enable_teams: true,
  enable_forms: true,
  enable_invoices: true,
  enable_timesheets: true,
  enable_payslips: true,
  enable_pay_contractors: true,
  enable_reimbursements: true,
  enable_mileage: true,
  enable_financials: true,
  enable_reports: true,
  enable_market_intel: true,
  enable_fleet: true,
  enable_activity_log: true,
};

// Optional pages an org admin can hide. Order = display order (two columns).
// Core pages (Dashboard, Scheduler, Clients, Staff, Settings) are intentionally
// omitted — hiding them would break the app.
const PAGE_TOGGLES = [
  { key: 'enable_templates',       label: 'Templates',        hint: 'Recurring shift templates.' },
  { key: 'enable_groups',          label: 'Group Services',   hint: 'Group / SIL shift services.' },
  { key: 'enable_teams',           label: 'Teams',            hint: 'Client & staff team grouping.' },
  { key: 'enable_forms',           label: 'Forms',            hint: 'Custom forms library.' },
  { key: 'enable_invoices',        label: 'Invoices',         hint: 'Client invoicing.' },
  { key: 'enable_timesheets',      label: 'Timesheets',       hint: 'Staff timesheet approvals.' },
  { key: 'enable_payslips',        label: 'Payslips',         hint: 'Generate staff payslips.' },
  { key: 'enable_pay_contractors', label: 'Pay Contractors',  hint: 'Contractor payment runs (AP).' },
  { key: 'enable_reimbursements',  label: 'Reimbursements',   hint: 'Staff expense reimbursements.' },
  { key: 'enable_mileage',         label: 'Mileage',          hint: 'Mileage tracking & claims.' },
  { key: 'enable_financials',      label: 'Financials',       hint: 'Financial dashboard & reports.' },
  { key: 'enable_reports',         label: 'Reports',          hint: 'Reporting hub & all reports.' },
  { key: 'enable_market_intel',    label: 'Market Intel',     hint: 'Competitor & market analysis.' },
  { key: 'enable_fleet',           label: 'Fleet',            hint: 'Vehicle fleet management.' },
  { key: 'enable_activity_log',    label: 'Activity Log',     hint: 'Org-wide audit activity log.' },
];

export default function FeatureSettings() {
  const [cfg, setCfg]     = useState(DEFAULTS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving]   = useState({});
  const reloadFeatures = useFeatureReload();

  useEffect(() => {
    api.get('/settings')
      .then(r => {
        const fs = r.data?.settings?.feature_settings;
        if (fs && Object.keys(fs).length) setCfg(prev => ({ ...prev, ...fs }));
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const set = (key, val) => setCfg(c => ({ ...c, [key]: val }));

  const saveSection = useCallback(async (section, keys) => {
    setSaving(s => ({ ...s, [section]: true }));
    try {
      const patch = {};
      keys.forEach(k => { patch[k] = cfg[k]; });
      await api.put('/settings', { feature_settings: { ...cfg, ...patch } });
      toast.success('Saved');
    } catch { toast.error('Failed to save'); }
    finally { setSaving(s => ({ ...s, [section]: false })); }
  }, [cfg]);

  const saveAll = useCallback(async (section) => {
    setSaving(s => ({ ...s, [section]: true }));
    try {
      await api.put('/settings', { feature_settings: cfg });
      persistFeatures(cfg);
      toast.success('Saved');
      reloadFeatures();
    } catch { toast.error('Failed to save'); }
    finally { setSaving(s => ({ ...s, [section]: false })); }
  }, [cfg, reloadFeatures]);

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <div className="w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
    </div>
  );

  return (
    <div className={PAGE}>
      <SettingsHeader title="Features" description="Enable or disable features for your organisation." />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 items-start">

        {/* ── LEFT COLUMN ─────────────────────────────────────────────────── */}
        <div className="space-y-5">

          {/* Medication Management */}
          <FeatureCard title="Medication Management" icon={Pill}
            onSave={() => saveAll('meds')} saving={saving.meds}>
            <FeatureRow label="Enable eMAR Medication Management"
              hint="Track and record medication administration electronically.">
              <Toggle checked={cfg.enable_emar} onChange={v => set('enable_emar', v)} />
            </FeatureRow>
          </FeatureCard>

          {/* Quotes */}
          <FeatureCard title="Quotes" icon={FileText}
            onSave={() => saveAll('quotes')} saving={saving.quotes}>
            <FeatureRow label="Enable Quotes" hint="Allow generating quotes for clients.">
              <Toggle checked={cfg.enable_quotes} onChange={v => set('enable_quotes', v)} />
            </FeatureRow>
            <FeatureRow label="Quote Title" hint="Label shown on client-facing quote documents.">
              <input value={cfg.quote_title} onChange={e => set('quote_title', e.target.value)}
                className="input-field !w-36 text-sm !py-1.5 text-right" />
            </FeatureRow>
            <FeatureRow label="Add quote terms to new quote" hint="Auto-attach default terms when creating a quote.">
              <Toggle checked={cfg.add_quote_terms} onChange={v => set('add_quote_terms', v)} />
            </FeatureRow>
            <div className="pt-2">
              <label className="block text-xs font-semibold text-slate-500 mb-1.5">Default Quote Terms</label>
              <textarea value={cfg.default_quote_terms}
                onChange={e => set('default_quote_terms', e.target.value)}
                rows={5} className="input-field w-full text-sm resize-y" />
            </div>
          </FeatureCard>

          {/* Task Settings */}
          <FeatureCard title="Task Settings" icon={ClipboardList}
            onSave={() => saveAll('tasks')} saving={saving.tasks}>
            <FeatureRow label="Enable Tasks" hint="Allow creating and assigning tasks to staff.">
              <Toggle checked={cfg.enable_tasks} onChange={v => set('enable_tasks', v)} />
            </FeatureRow>

            {/* Durations */}
            <div className="pt-3">
              <div className="flex items-center justify-between mb-1">
                <p className="text-sm font-semibold text-slate-700">Duration</p>
              </div>
              <p className="text-xs text-slate-400 mb-1">Define time blocks available when scheduling tasks.</p>
              <InlineList
                items={cfg.task_durations}
                onAdd={v => set('task_durations', [...cfg.task_durations, v])}
                onDelete={i => set('task_durations', cfg.task_durations.filter((_, idx) => idx !== i))}
                placeholder="e.g. 30 minutes, 1 hour…"
              />
            </div>

            {/* Support Categories */}
            <div className="pt-3">
              <p className="text-sm font-semibold text-slate-700 mb-1">Support Categories</p>
              <p className="text-xs text-slate-400 mb-1">NDIS support categories used to classify tasks.</p>
              <InlineList
                items={cfg.task_support_categories}
                onAdd={v => set('task_support_categories', [...cfg.task_support_categories, v])}
                onDelete={i => set('task_support_categories', cfg.task_support_categories.filter((_, idx) => idx !== i))}
                placeholder="e.g. Daily Activities, Community Access…"
              />
            </div>

            {/* Invoice Settings */}
            <div className="pt-3 border-t border-slate-100 mt-2">
              <p className="text-sm font-semibold text-slate-700 mb-2">Invoice Settings</p>
              <div className="mb-3">
                <label className="block text-xs font-semibold text-slate-500 mb-1.5">Task invoice item format</label>
                <input value={cfg.task_invoice_format}
                  onChange={e => set('task_invoice_format', e.target.value)}
                  className="input-field w-full text-xs font-mono" />
                <p className="text-[11px] text-slate-400 mt-1">
                  Variables: <code className="bg-slate-100 px-1 rounded">{'{client_full_name}'}</code>{' '}
                  <code className="bg-slate-100 px-1 rounded">{'{date}'}</code>{' '}
                  <code className="bg-slate-100 px-1 rounded">{'{start_time}'}</code>{' '}
                  <code className="bg-slate-100 px-1 rounded">{'{end_time}'}</code>{' '}
                  <code className="bg-slate-100 px-1 rounded">{'{pricebook}'}</code>{' '}
                  <code className="bg-slate-100 px-1 rounded">{'{task_name}'}</code>
                </p>
              </div>
              <FeatureRow label="Require support category on tasks"
                hint="Users must select a support category when creating or editing a task.">
                <Toggle checked={cfg.require_support_category} onChange={v => set('require_support_category', v)} />
              </FeatureRow>
            </div>

            {/* Task Types & Prices */}
            <div className="pt-3 border-t border-slate-100 mt-2">
              <p className="text-sm font-semibold text-slate-700 mb-2">Tasks Types &amp; Prices</p>
              <InlineList
                items={cfg.task_types}
                onAdd={v => set('task_types', [...cfg.task_types, v])}
                onDelete={i => set('task_types', cfg.task_types.filter((_, idx) => idx !== i))}
                placeholder="e.g. Assessment, Support Coordination…"
                addLabel="Add Task Type"
              />
            </div>
          </FeatureCard>

        </div>

        {/* ── RIGHT COLUMN ────────────────────────────────────────────────── */}
        <div className="space-y-5">

          {/* Incident Management */}
          <FeatureCard title="Incident Management" icon={AlertTriangle}
            onSave={() => saveAll('incidents')} saving={saving.incidents}>
            <FeatureRow label="Enable Incident Management" hint="Record and manage incidents across your organisation.">
              <Toggle checked={cfg.enable_incidents} onChange={v => set('enable_incidents', v)} />
            </FeatureRow>
            <FeatureRow label="Enable Incident Management in Carers App"
              hint="Allow carers to log incidents directly from the mobile app.">
              <Toggle checked={cfg.enable_incidents_carers_app} onChange={v => set('enable_incidents_carers_app', v)} />
            </FeatureRow>
          </FeatureCard>

          {/* Facilities */}
          <FeatureCard title="Facilities" icon={Building2}
            onSave={() => saveAll('facilities')} saving={saving.facilities}>
            <FeatureRow label="Enable Facilities" hint="Manage group homes, day programs and other facilities.">
              <Toggle checked={cfg.enable_facilities} onChange={v => set('enable_facilities', v)} />
            </FeatureRow>
          </FeatureCard>

          {/* Publish Shifts */}
          <FeatureCard title="Publish Shifts" icon={Calendar}
            onSave={() => saveAll('shifts')} saving={saving.shifts}>
            <FeatureRow label="Publish shifts">
              <Toggle checked={cfg.publish_shifts} onChange={v => set('publish_shifts', v)} />
            </FeatureRow>
            <p className="text-xs text-slate-400 pt-1">
              Plan your schedule with more control — staff only see shifts once you publish them.
            </p>
          </FeatureCard>

          {/* Advanced Break Tracking */}
          <FeatureCard title="Advanced Break Tracking Settings" icon={Clock}
            onSave={() => saveAll('breaks')} saving={saving.breaks}>
            <FeatureRow label="Use advanced break tracking settings"
              hint="Enable granular break rules per shift type or award.">
              <Toggle checked={cfg.advanced_break_tracking} onChange={v => set('advanced_break_tracking', v)} />
            </FeatureRow>
          </FeatureCard>

          {/* Offline Mobile */}
          <FeatureCard title="Offline Mobile" icon={Wifi}
            onSave={() => saveAll('offline')} saving={saving.offline}>
            <FeatureRow label="Offline clocking"
              hint="Allow the mobile app to sync clock-in/out data in offline mode.">
              <Toggle checked={cfg.offline_clocking} onChange={v => set('offline_clocking', v)} />
            </FeatureRow>
          </FeatureCard>

          {/* Instant Messaging */}
          <FeatureCard title="Instant Messaging" icon={MessageSquare}
            onSave={() => saveAll('chat')} saving={saving.chat}>
            <FeatureRow label="Enable general chat"
              hint="Allow staff-wide communication through a general group chat.">
              <Toggle checked={cfg.enable_general_chat} onChange={v => set('enable_general_chat', v)} />
            </FeatureRow>
            <FeatureRow label="Enable team-based chats"
              hint="Allow staff to communicate within their assigned teams.">
              <Toggle checked={cfg.enable_team_chats} onChange={v => set('enable_team_chats', v)} />
            </FeatureRow>
            <FeatureRow label="Enable direct messaging"
              hint="Allow staff to communicate with each other directly.">
              <Toggle checked={cfg.enable_direct_messaging} onChange={v => set('enable_direct_messaging', v)} />
            </FeatureRow>
            <FeatureRow label="Send Care Signal Alerts via Chat"
              hint="Allow admins to subscribe to real-time Care Signal Alerts.">
              <Toggle checked={cfg.enable_care_signal_alerts_chat} onChange={v => set('enable_care_signal_alerts_chat', v)} />
            </FeatureRow>
          </FeatureCard>

        </div>
      </div>

      {/* ── Pages & Navigation (full width) ─────────────────────────────── */}
      <FeatureCard title="Pages & Navigation" icon={LayoutGrid}
        onSave={() => saveAll('pages')} saving={saving.pages}>
        <p className="text-xs text-slate-400 -mt-1 mb-1">
          Turn off pages your organisation doesn't use. A disabled page is hidden from the
          menu <span className="font-semibold text-slate-500">and blocked from direct access</span>.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8">
          {PAGE_TOGGLES.map(({ key, label, hint }) => (
            <FeatureRow key={key} label={label} hint={hint}>
              <Toggle checked={cfg[key] !== false} onChange={v => set(key, v)} />
            </FeatureRow>
          ))}
        </div>
      </FeatureCard>
    </div>
  );
}
